import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Delete as DeleteIcon, Download as DownloadIcon, Gavel as CaseIcon, WarningAmber as StaleIcon } from "@mui/icons-material";
import { useAppDispatch } from "../../store/hooks";
import { setSnackAction } from "../../store/slices/snackSlice";
import { useAuth } from "../../contexts/authContext";
import lensService, { type RunSummary } from "../../services/lensService";
import useRuns from "./hooks/useRuns";
import useLensMutation from "./hooks/useLensMutation";
import ConfirmDialog from "./ConfirmDialog";
import { formatDuration, formatWhen, isRunBy, runBy } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    toolbar: { display: "flex", gap: theme.spacing(2), alignItems: "center", flexWrap: "wrap", marginBottom: theme.spacing(4) },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
    row: { cursor: "pointer" },
    muted: { color: "#777" },
    chips: { display: "flex", gap: theme.spacing(1), alignItems: "center", flexWrap: "wrap" },
}));

type Filter = "all" | "client" | "test";
type Scope = "all" | "mine";

const head = { fontWeight: 600 };
const rowsPerPageOptions = [10, 25, 50, 100];

const RunTable = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAuth();
    const runs = useRuns();
    const archiveRun = useLensMutation(lensService.archiveRun);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState<Filter>("all");
    const [scope, setScope] = useState<Scope>("all");
    const [deleting, setDeleting] = useState<RunSummary | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const all = runs.data ?? [];
    const term = search.trim().toLowerCase();
    const rows = all.filter(
        (run) =>
            (filter === "all" || run.isTest === (filter === "test")) &&
            (scope === "all" || isRunBy(run, user?.details.email)) &&
            (!term ||
                [run.clientName, run.documentName, run.playbookName].some((value) => value?.toLowerCase().includes(term))),
    );
    // Clamped, as a delete or refetch can shrink the list under the current page.
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = rows.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    const downloadMarkedUp = (run: RunSummary) =>
        lensService
            .downloadMarkedUp(run)
            .catch(() => dispatch(setSnackAction("Could not download the marked-up copy. Please try again.", "error")));

    const count = (n: number) => (n > 0 ? n : <span className={classes.muted}>—</span>);

    return (
        <>
            <Box className={classes.toolbar}>
                <TextField
                    size="small"
                    label="Search client, document or playbook"
                    style={{ minWidth: 300 }}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filter}
                    onChange={(_e, val: Filter | null) => {
                        if (!val) return;
                        setFilter(val);
                        setPage(0);
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="client">Client work</ToggleButton>
                    <ToggleButton value="test">Tests</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={scope}
                    onChange={(_e, val: Scope | null) => {
                        if (!val) return;
                        setScope(val);
                        setPage(0);
                    }}
                >
                    <ToggleButton value="all">Everyone</ToggleButton>
                    <ToggleButton value="mine">My runs</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper>
                {runs.isLoading ? (
                    <Box className={classes.center}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body1">
                            {runs.isError
                                ? "Could not load the run log."
                                : all.length === 0
                                  ? "No runs yet — review a document above."
                                  : `No runs match this filter — ${all.length} hidden.`}
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={head}>Document</TableCell>
                                <TableCell style={head}>Client</TableCell>
                                <TableCell style={head}>Playbook</TableCell>
                                <TableCell style={head}>When</TableCell>
                                <TableCell style={head}>Run by</TableCell>
                                <TableCell style={head} align="right">Critical</TableCell>
                                <TableCell style={head} align="right">Material</TableCell>
                                <TableCell style={head} align="right">Preference</TableCell>
                                <TableCell style={head} align="right">Omissions</TableCell>
                                <TableCell style={head} align="right">Not assessed</TableCell>
                                <TableCell style={head} align="right">Time</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageRows.map((run) => (
                                <TableRow
                                    key={run.identifier}
                                    hover
                                    className={classes.row}
                                    onClick={() => navigate(`/lens/runs/${encodeURIComponent(run.identifier)}`)}
                                >
                                    <TableCell>
                                        <Box className={classes.chips}>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {run.documentName}
                                            </Typography>
                                            {run.isTest && <Chip size="small" variant="outlined" color="secondary" label="test" />}
                                            {run.source === "addin" && <Chip size="small" variant="outlined" label="Word add-in" />}
                                            {run.caseNestCaseId != null && (
                                                <Tooltip title="Reviewed from a CaseNest case">
                                                    <CaseIcon fontSize="small" color="action" aria-label="CaseNest" style={{ verticalAlign: "middle", marginLeft: 4 }} />
                                                </Tooltip>
                                            )}
                                            {run.status === "running" && <Chip size="small" color="primary" label="Running" />}
                                            {run.status === "running" && run.progress && (
                                                <Typography variant="caption" className={classes.muted}>
                                                    {run.progress}
                                                </Typography>
                                            )}
                                            {run.status === "failed" && (
                                                <Tooltip title={run.error ?? ""}>
                                                    <Chip size="small" variant="outlined" color="error" label="Failed" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.muted}>{run.clientName || "—"}</TableCell>
                                    <TableCell className={classes.muted}>
                                        {run.playbookName} v{run.playbookVersion}
                                    </TableCell>
                                    <TableCell className={classes.muted} style={{ whiteSpace: "nowrap" }}>
                                        {formatWhen(run.startedOn)}
                                    </TableCell>
                                    <TableCell className={classes.muted} style={{ whiteSpace: "nowrap" }}>
                                        {runBy(run)}
                                    </TableCell>
                                    <TableCell align="right">{count(run.counts.critical)}</TableCell>
                                    <TableCell align="right">{count(run.counts.material)}</TableCell>
                                    <TableCell align="right">{count(run.counts.preference)}</TableCell>
                                    <TableCell align="right">{count(run.counts.omissions)}</TableCell>
                                    <TableCell align="right">{count(run.counts.notAssessed)}</TableCell>
                                    <TableCell align="right" className={classes.muted}>
                                        {formatDuration(run.durationMs)}
                                    </TableCell>
                                    <TableCell align="right" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                                        {run.hasRedline && (
                                            <Tooltip
                                                describeChild
                                                title={
                                                    // A Word run's newer decisions are in the colleague's document, not missing from Lens.
                                                    run.redlineStale && run.source === "addin"
                                                        ? "Download the copy last saved from Word. Decisions made in Word since then are in the colleague's document."
                                                        : run.redlineStale
                                                          ? "Decisions have changed since this copy was generated. Open the run and generate it again before you send it."
                                                          : "Download the marked-up copy"
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    color={run.redlineStale && run.source !== "addin" ? "warning" : "primary"}
                                                    aria-label={`Download the marked-up copy of ${run.documentName}`}
                                                    onClick={() => downloadMarkedUp(run)}
                                                >
                                                    {run.redlineStale && run.source !== "addin" ? (
                                                        <StaleIcon fontSize="small" />
                                                    ) : (
                                                        <DownloadIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Delete run">
                                            <IconButton
                                                size="small"
                                                aria-label={`Delete the run of ${run.documentName}`}
                                                disabled={run.status === "running"}
                                                onClick={() => setDeleting(run)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={rowsPerPageOptions}
                                    count={rows.length}
                                    rowsPerPage={rowsPerPage}
                                    page={currentPage}
                                    onPageChange={(_e, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                )}
            </Paper>

            <ConfirmDialog
                open={!!deleting}
                title="Delete this run?"
                confirmLabel="Delete"
                destructive
                onClose={() => setDeleting(null)}
                onConfirm={() => {
                    if (!deleting) return;
                    archiveRun.mutate(deleting.identifier, {
                        onSuccess: () => dispatch(setSnackAction("Run deleted", "success")),
                    });
                }}
            >
                {deleting?.source === "addin"
                    ? `The summary of "${deleting?.documentName}" is removed from the run log.`
                    : `The run of "${deleting?.documentName}", its findings and its stored documents are removed from the run log.`}
            </ConfirmDialog>
        </>
    );
};

export default RunTable;
